import {
  PublicRequester,
  Requester,
  apiClientWithTenantIdHeader,
} from '@/features/api/client.service.js'
import { filterUndefined } from '@/features/api/filterUndefined.js'
import { makeApiRequest } from '@/features/api/services/utils/makeApiRequest.js'
import { makeFormData } from '@/features/api/services/utils/makeFormData.js'

export const authApiEndpoints = {
  login: '/Authorization/Authentication/Login',
  forgetMyPassword: '/Authorization/Authentication/ForgetMyPassword',
  socialRegister: '/Authorization/Authentication/SocialRegister',
  socialConnect: '/Authorization/Authentication/SocialConnect',
  myDetails: '/Authorization/Authentication/MyDetails',
  newTenant: '/Authorization/Authentication/NewTenant',
  register: '/Authorization/Authentication/Register',
  apis: '/Authorization/API',
  roleApi: '/Authorization/RoleAPI',
  roleApiAdd: '/Authorization/RoleAPI/Add',
  roleApiDelete: '/Authorization/RoleAPI/Delete',
  profileOnlineAccess: '/Authorization/Authentication/ProfileOnlineAccess',
  profileSuspendAccess: '/Authorization/Authentication/ProfileSuspendAccess',
  updateMyPassword: '/Authorization/Authentication/UpdateMyPassword',
  osiTenantMailerMigrate: '/Authorization/Authentication/osiTenantMailerMigrate',
  osiTenantMailerMigrateStatus: '/Authorization/Authentication/osiTenantMailerMigrateStatus',
  getOrgMembers: '/Authorization/Authentication/GetOrgMembers',
  makeTenantOrg: '/Authorization/Authentication/MakeTenantOrg',
  addMemberToOrg: '/Authorization/Authentication/AddMemberToOrg',
  removeMemberFromOrg: '/Authorization/Authentication/RemoveMemberToOrg',
  getProfileTenantList: '/Authorization/Authentication/GetProfileTenantList',
  addTenantToProfile: '/Authorization/Authentication/AddTenantToProfile',
  removeTenantFromProfile: '/Authorization/Authentication/RemoveTenantFromProfile',
  osiGetTenantLogo: '/Authorization/Authentication/osiGetTenantLogo',
  osiGetTenantHeader: '/Authorization/Authentication/osiGetTenantHeader',
  logout: '/Authorization/Authentication/Logout',
  getMobileVersion: '/Authorization/Authentication/GetMobileVersion',
  updateMobileVersion: '/Authorization/Authentication/UpdateMobileVersion',
  mobileProfileList: '/Authorization/Authentication/MobileProfileList',
  removeMobileAccount: '/Authorization/Authentication/RemoveMobileAccount',
}

export const RegisterType = {
  MemberTrial: 0,
  TenantUser: 1,
}

export const SocialLoginType = {
  None: 0,
  Google: 1,
  Facebook: 2,
}

export const AuthApi = {
  loginWithEmail: async props =>
    makeApiRequest(async () => {
      const { email, password, code, tenantId } = props

      const body = filterUndefined({
        UserName: email,
        Password: password,
        Code: code,
        Tenant_Id: tenantId,
        SocialToken: undefined,
        SocialLogin: SocialLoginType.None,
      })

      return PublicRequester.post({
        url: authApiEndpoints.login,
        data: makeFormData(body),
      })
    })(),

  forgetMyPassword: async props =>
    makeApiRequest(async () => {
      const { email } = props

      const body = filterUndefined({
        Email: email,
      })

      return PublicRequester.post({
        url: authApiEndpoints.forgetMyPassword,
        data: makeFormData(body),
      })
    })(),

  socialLogin: async props =>
    makeApiRequest(async () => {
      const { socialLoginType, socialToken, tenantId, registerType } = props

      const body = filterUndefined({
        Tenant_Id: tenantId,
        Token: undefined,
        SocialLogin: socialLoginType,
        SocialToken: socialToken,
        RegisterType: registerType,
        IsoLanguageCode: undefined,
      })

      return PublicRequester.post({
        url: authApiEndpoints.login,
        data: makeFormData(body),
      })
    })(),

  socialRegister: async props =>
    makeApiRequest(async () => {
      const { socialLoginType, token, tenantId, registerType } = props

      const body = filterUndefined({
        Tenant_Id: tenantId,
        Token: token,
        SocialLogin: socialLoginType,
        RegisterType: registerType,
        IsoLanguageCode: undefined,
      })

      return PublicRequester.post({
        url: authApiEndpoints.socialRegister,
        data: makeFormData(body),
      })
    })(),

  socialConnect: async props =>
    makeApiRequest(async () => {
      const { socialLoginType, token } = props

      const body = filterUndefined({
        Token: token,
        SocialLogin: socialLoginType,
      })

      return PublicRequester.post({
        url: authApiEndpoints.socialConnect,
        data: makeFormData(body),
      })
    })(),

  myDetails: async () =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.myDetails,
      }),
    )(),

  createNewTenant: async () =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.newTenant,
      }),
    )(),

  register: async data =>
    makeApiRequest(async () => {
      const { email, password, firstName, lastName, tenantId, registerType } = data

      return Requester.post({
        url: authApiEndpoints.register,
        data: makeFormData({
          UserName: email,
          Password: password,
          FirstName: firstName,
          LastName: lastName,
          Tenant_Id: tenantId ?? '',
          RegisterType: registerType,
        }),
      })
    })(),

  apisList: async ({ numberOfObjectsPerPage = 999999 } = {}) =>
    makeApiRequest(() =>
      Requester.put({
        url: authApiEndpoints.apis,
        data: makeFormData({ numberOfObjectsPerPage }),
      }),
    )(),

  // roleId uuid
  roleApiList: async roleId =>
    makeApiRequest(() =>
      Requester.put({
        url: authApiEndpoints.roleApi,
        queryParams: { RoleId: roleId },
      }),
    )(),

  // roleId int
  // apiId int
  roleApiAdd: async ({ roleId, apiId }) =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.roleApiAdd,
        data: makeFormData({
          Role_Id: roleId,
          Api_Id: apiId,
        }),
      }),
    )(),

  // id uuid
  roleApiDelete: async id =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.roleApiDelete,
        queryParams: { Id: id },
      }),
    )(),

  // profileId uuid
  profileOnlineAccess: async (profileId, options = {}) =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.profileOnlineAccess,
        queryParams: {
          ProfileId: profileId,
          ...(Object.hasOwn(options, 'isSuperAdminLimited') && {
            isSuperAdminLimited: options.isSuperAdminLimited,
          }),
        },
        ...(options.tenantId && {
          headers: { 'tenant-id': options.tenantId },
        }),
        ...(Object.hasOwn(options, 'superAdminLimitedRoleGroupId') && {
          data: makeFormData({
            SuperAdminLimitedRoleGroupId: options.superAdminLimitedRoleGroupId,
          }),
        }),
      }),
    )(),

  // profileId uuid
  profileSuspendAccess: async profileId =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.profileSuspendAccess,
        queryParams: { ProfileId: profileId },
      }),
    )(),

  updateMyPassword: async ({ previousPassword, password }) =>
    makeApiRequest(async () => {
      const body = filterUndefined({
        previousPassword,
        password,
      })

      return Requester.post({
        url: authApiEndpoints.updateMyPassword,
        data: makeFormData(body),
      })
    })(),

  osiTenantMailerMigrate: async ({ tenantId, isMigrated }) =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.osiTenantMailerMigrate,
        data: makeFormData({ TenantId: tenantId, isMigrated }),
      }),
    )(),

  osiTenantMailerMigrateStatus: async tenantId =>
    makeApiRequest(() =>
      Requester.put({
        url: authApiEndpoints.osiTenantMailerMigrateStatus,
        data: makeFormData({ TenantId: tenantId }),
      }),
    )(),

  getOrgMembers: async orgTenantId =>
    makeApiRequest(() =>
      Requester.put({
        url: authApiEndpoints.getOrgMembers,
        data: makeFormData({ OrgTenantId: orgTenantId }),
        headers: { 'Tenant-Id': orgTenantId },
        apiClient: apiClientWithTenantIdHeader(orgTenantId),
      }),
    )(),

  makeTenantOrg: async (orgTenantId, isOrg) =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.makeTenantOrg,
        queryParams: { TenantId: orgTenantId },
        data: makeFormData({ isOrg }),
      }),
    )(),

  addMemberToOrg: async (orgTenantId, tenantId) =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.addMemberToOrg,
        queryParams: { TenantId: tenantId, OrgTenantId: orgTenantId },
        headers: { 'Tenant-Id': orgTenantId },
        apiClient: apiClientWithTenantIdHeader(orgTenantId),
      }),
    )(),

  removeMemberFromOrg: async (orgTenantId, tenantId) =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.removeMemberFromOrg,
        queryParams: { TenantId: tenantId, OrgTenantId: orgTenantId },
        headers: { 'Tenant-Id': orgTenantId },
        apiClient: apiClientWithTenantIdHeader(orgTenantId),
      }),
    )(),

  getProfileTenantList: async profileId =>
    makeApiRequest(() =>
      Requester.put({
        url: authApiEndpoints.getProfileTenantList,
        queryParams: { ProfileId: profileId },
      }),
    )(),

  addTenantToProfile: async (profileId, tenantId) =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.addTenantToProfile,
        queryParams: { ProfileId: profileId },
        data: makeFormData({ TenantId: tenantId }),
      }),
    )(),

  removeTenantFromProfile: async (profileId, tenantId) =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.removeTenantFromProfile,
        queryParams: { ProfileId: profileId },
        data: makeFormData({ TenantId: tenantId }),
      }),
    )(),

  osiGetTenantLogo: async () =>
    makeApiRequest(() =>
      Requester.put({
        url: authApiEndpoints.osiGetTenantLogo,
      }),
    )(),

  osiGetTenantHeader: async () =>
    makeApiRequest(() =>
      Requester.put({
        url: authApiEndpoints.osiGetTenantHeader,
      }),
    )(),

  logout: async () =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.logout,
      }),
    )(),

  getMobileVersion: async () =>
    makeApiRequest(() =>
      Requester.put({
        url: authApiEndpoints.getMobileVersion,
      }),
    )(),

  updateMobileVersion: async ({ mobileVersion, mobileIOSUrl, mobileAndroidUrl }) =>
    makeApiRequest(() => {
      const body = filterUndefined({
        MobileVersion: mobileVersion,
        MobileIOSURL: mobileIOSUrl,
        MobileAndroidURL: mobileAndroidUrl,
      })

      return Requester.post({
        url: authApiEndpoints.updateMobileVersion,
        data: makeFormData(body),
      })
    })(),

  mobileProfileList: async ({ numberOfObjectsPerPage, pageNumber, search }) =>
    makeApiRequest(() => {
      const body = filterUndefined({
        numberOfObjectsPerPage,
        pageNumber,
        search,
      })

      return Requester.put({
        url: authApiEndpoints.mobileProfileList,
        data: makeFormData(body),
      })
    })(),

  removeMobileAccount: async mobileProfileId =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.removeMobileAccount,
        queryParams: { MobileProfileId: mobileProfileId },
      }),
    )(),
}
